/**
 * Analyzer: Thesis Validation (NEW)
 *
 * If the session has a thesis text, check whether the trades that
 * actually happened are consistent with it. This is best-effort text
 * matching — we extract directional intent ("long", "short", "fade",
 * "buy", "sell", "reject", "dip") and symbol mentions, then compare
 * against the actual trades.
 *
 * Philosophy: the thesis is a promise the trader made to themselves
 * at open. A session full of off-thesis trades is a discipline leak
 * even if it's profitable.
 */

import { CATEGORY, SEVERITY, makeObservation, makeAction, ACTION_TYPE } from '../types.js';
import { sameFuturesFamily } from '../../../utils/futuresFamilies.js';

export const key = 'thesisValidation';

// Directional keyword families — extend over time as user vocabulary evolves.
const LONG_WORDS  = /\b(long|buy|bought|bid|bull|bullish|dip|support|reclaim|breakout\s+long|pullback\s+buy)\b/i;
const SHORT_WORDS = /\b(short|sell|sold|offer|bear|bearish|fade|reject|breakdown|lower\s+high|supply)\b/i;

/**
 * Trading-vocabulary stoplist — tokens that LOOK like ticker symbols
 * (2-5 uppercase letters, optional trailing digit) but are actually
 * indicator names, risk metrics, or concept labels. The original
 * extractor naively treated "EMA" / "VWAP" / "MAE" as symbol mentions,
 * which caused 100%-off-thesis false positives on theses like
 * "First Touch of 15M 19 EMA".
 *
 * Categories: indicators, risk metrics, concept labels, session/macro
 * abbreviations, order types, time abbreviations, common acronyms.
 *
 * NOTE: this is the PESSIMISTIC fallback — when we have allTrades, we
 * also use the user's actually-traded symbol set as the SOURCE OF TRUTH
 * for what counts as a real ticker. A new token that's neither in the
 * traded set NOR in the stoplist still survives, so a brand-new ticker
 * mention isn't lost.
 */
const TRADING_VOCABULARY = new Set([
  // Indicators
  'EMA', 'SMA', 'WMA', 'VWAP', 'AVWAP', 'ATR', 'RSI', 'MACD', 'ADX',
  'OBV', 'CCI', 'BBAND', 'BB', 'KC', 'TEMA', 'DEMA',
  // Risk / outcome metrics
  'MAE', 'MFE', 'PNL', 'PL', 'WR', 'PF', 'BE', 'TP', 'SL',
  'STP', 'LMT', 'MKT', 'OCO', 'OSO',
  // Smart-money / ICT / SMC concept labels
  'OB', 'FVG', 'BPR', 'IB', 'OTE', 'MSS', 'MSB', 'BOS', 'CHOCH',
  'PD', 'PDA', 'PMH', 'PML', 'PDH', 'PDL', 'SMT', 'NDOG',
  'POC', 'VAH', 'VAL', 'VPOC', 'VPVR', 'IBL', 'IBH',
  'HOD', 'LOD', 'OG', 'FA', 'OPR', 'ORB',
  // Sessions / time
  'AM', 'PM', 'ET', 'PT', 'EST', 'PST', 'CST', 'UTC',
  'PRE', 'AH', 'RTH', 'ETH',
  // Session geography (uppercase tokens that look like 2-char tickers but
  // are session shorthand in real-world theses — e.g. "NY Open", "EU Close")
  'NY', 'EU', 'UK', 'JP', 'CN', 'HK', 'AU', 'ASIA', 'EURO', 'OPEN', 'CLOSE',
  // Macro / events
  'FOMC', 'CPI', 'PCE', 'PPI', 'NFP', 'GDP', 'ISM', 'PMI',
  'JOLTS', 'ADP', 'FED',
  // Generic frame / scope
  'TF', 'HTF', 'LTF', 'SR', 'SD', 'EQ',
  // Periods / calendar
  'EOD', 'EOM', 'EOY', 'YTD', 'MTD', 'QTD', 'WTD',
  // Plain English caps
  'AND', 'OR', 'BUT', 'NOT', 'YES', 'NO', 'OK', 'TBD', 'TBA',
  'FYI', 'ETA', 'ASAP', 'AKA', 'IE', 'EG', 'VS',
  'USD', 'EUR', 'GBP', 'JPY', 'CAD',
]);

/**
 * Extract directional intent from the thesis. Returns 'long', 'short',
 * 'either', or null if no clear signal.
 */
const extractIntent = (text) => {
  if (!text || typeof text !== 'string') return null;
  const long = LONG_WORDS.test(text);
  const short = SHORT_WORDS.test(text);
  if (long && short) return 'either';
  if (long) return 'long';
  if (short) return 'short';
  return null;
};

/**
 * Extract symbol mentions from the thesis. Looks for 2-5 char all-caps
 * tokens (matches MNQ, ES, NQ, MES, MNQM6, etc.), then filters out:
 *   - Trading vocabulary (EMA, VWAP, MAE, etc.)
 *   - Anything not in the user's known traded-symbol set, IF that set
 *     is provided. Tokens not in the vocabulary stoplist AND not in the
 *     known-symbols set are still kept (benefit of the doubt for new
 *     tickers a user mentions before they've traded them).
 *
 * Returns an uppercase Set.
 *
 * @param {string} text
 * @param {Set<string>} [knownSymbols]  Optional — set of symbols the
 *                                      user has actually traded. Used
 *                                      to disambiguate ambiguous tokens.
 */
const extractSymbols = (text, knownSymbols = null) => {
  if (!text || typeof text !== 'string') return new Set();
  const matches = text.match(/\b[A-Z]{2,5}\d?\b/g) || [];
  const out = new Set();
  for (const raw of matches) {
    const tok = raw.toUpperCase();
    // Always keep if it's a known traded symbol — that's the strongest signal.
    if (knownSymbols && knownSymbols.has(tok)) {
      out.add(tok);
      continue;
    }
    // Drop trading-vocabulary tokens unless the user has actually traded
    // a symbol of that name (handled above).
    if (TRADING_VOCABULARY.has(tok)) continue;
    // Otherwise, accept — a token like "AAPL" or "GOOG" we haven't seen
    // yet still gets treated as a probable symbol mention.
    out.add(tok);
  }
  return out;
};

/**
 * Build the user's known-traded-symbol set from allTrades. Cheap to
 * compute; O(n). Returns an uppercase Set.
 */
const knownSymbolSet = (allTrades) => {
  if (!Array.isArray(allTrades)) return new Set();
  const s = new Set();
  for (const t of allTrades) {
    const sym = (t?.symbol || '').toString().toUpperCase().trim();
    if (sym) s.add(sym);
  }
  return s;
};

export const analyze = (ctx) => {
  const { session, trades, enriched, summary, allTrades } = ctx;
  if (!session || !session.thesis || typeof session.thesis !== 'string') return null;
  const thesisText = session.thesis.trim();
  if (thesisText.length < 12) return null; // Too short to infer intent

  const intent = extractIntent(thesisText);
  // Use the user's actually-traded symbol set as ground truth so
  // indicator names and concept labels in the thesis don't get
  // misread as ticker mentions.
  const knownSymbols = knownSymbolSet(allTrades);
  const thesisSymbols = extractSymbols(thesisText, knownSymbols);

  if (!intent && thesisSymbols.size === 0) return null;
  if (!Array.isArray(trades) || trades.length === 0) return null;

  // Direction check
  let offDirection = 0;
  if (intent && intent !== 'either') {
    offDirection = trades.filter(t => t.direction && t.direction !== intent).length;
  }

  // Symbol check — only counts if thesis mentioned specific symbols.
  //
  // KNOWN-SYMBOL INTERSECTION DEFENSE (against the "phantom symbol drift"
  // bug class): only treat extracted thesis tokens as "actual symbol
  // constraints" if they appear in the user's known-traded symbol set.
  //
  // The bug this fixes: a thesis like "Bounce of 15M 21EMA in first 30
  // min of NY Open" caused every MNQ trade to be flagged as off-symbol
  // because "NY" passed the uppercase-ticker regex and wasn't in the
  // per-token vocab stoplist. The intersection here neutralizes any
  // surviving phantom — "NY" is not a ticker the user has ever traded,
  // so it can't act as a drift constraint.
  //
  // We deliberately do NOT short-circuit on "all trades use one symbol"
  // alone: that would also suppress the legitimate "thesis explicitly
  // said MNQ, trader took all ES" case (all-ES is one symbol but it's
  // still drift from the stated plan).
  //
  // Tradeoff with the intersection: a trader mentioning a brand-new
  // ticker in the thesis BEFORE their first trade on it won't get drift
  // flags. Acceptable — "I haven't traded this yet" is exactly when
  // symbol-drift IS expected and shouldn't be punished.
  const realThesisSymbols = new Set(
    [...thesisSymbols].filter(s => knownSymbols.has(s))
  );
  let offSymbol = 0;
  if (realThesisSymbols.size > 0) {
    offSymbol = trades.filter(t => {
      const sym = (t.symbol || '').toUpperCase();
      if (!sym) return false;
      // Direct symbol match — definitely not drift.
      if (realThesisSymbols.has(sym)) return false;
      // Futures-family match — MNQ↔NQ (strict micro/mini pair) AND
      // MNQ↔MES (cross-index correlation group: indices co-move enough
      // that a trader using both isn't drifting). User explicitly
      // confirmed: "MNQ and MES travel in parallel much of the time."
      // See utils/futuresFamilies.js for the catalog + semantics.
      for (const thesisSym of realThesisSymbols) {
        if (sameFuturesFamily(sym, thesisSym)) return false;
      }
      return true;
    }).length;
  }

  const totalOff = offDirection + offSymbol;
  if (totalOff === 0) {
    // Thesis fully respected — success observation (useful positive reinforcement)
    if (summary.total > 0 && trades.length >= 3) {
      return makeObservation({
        id: `${key}:${session.id}:aligned`,
        analyzer: key,
        scope: 'session',
        scopeId: session.id,
        severity: SEVERITY.SUCCESS,
        category: CATEGORY.PROCESS,
        title: `Trades stayed on thesis end-to-end`,
        detail: `${trades.length} trades, all matched the session plan — discipline signal.`,
        action: 'Note this in Lessons — "stayed in lane" is a repeatable skill, not luck.',
        actions: [
          makeAction({
            type: ACTION_TYPE.ADD_LESSON_TO_SESSION,
            label: 'Save to Lessons',
            payload: {
              sessionId: session.id,
              lesson: `Stayed on thesis end-to-end across ${trades.length} trades — discipline pattern worth repeating`,
            },
            doneLabel: 'Saved ✓',
          }),
        ],
        evidence: { intent, thesisSymbols: Array.from(thesisSymbols), tradesChecked: trades.length },
        confidence: 0.65,
        priority: 55
      });
    }
    return null;
  }

  // Off-thesis trades exist
  const offPct = totalOff / trades.length;
  if (offPct < 0.25) return null; // Too few to call it a drift

  // Compute the off-thesis P&L slice (did the drift help or hurt?)
  // Uses the SAME family-aware match logic as the count above so the
  // P&L attribution can't disagree with the count. (If the count says 0
  // trades drifted on symbol, the slice MUST also be 0 for symbol.)
  let offPnl = 0;
  if (Array.isArray(enriched)) {
    const offIds = new Set();
    trades.forEach(t => {
      const isOffDir = intent && intent !== 'either' && t.direction && t.direction !== intent;
      let isOffSym = false;
      if (realThesisSymbols.size > 0 && t.symbol) {
        const sym = t.symbol.toUpperCase();
        const directHit = realThesisSymbols.has(sym);
        const familyHit = !directHit
          && [...realThesisSymbols].some(thesisSym => sameFuturesFamily(sym, thesisSym));
        isOffSym = !directHit && !familyHit;
      }
      if (isOffDir || isOffSym) offIds.add(t.id);
    });
    offPnl = enriched
      .filter(x => offIds.has(x.trade.id))
      .reduce((s, x) => s + x.netPnl, 0);
  }

  const severity = offPct >= 0.5 ? SEVERITY.WARNING : SEVERITY.INFO;
  const pctLabel = `${Math.round(offPct * 100)}%`;

  const driftDesc = offDirection > 0 && offSymbol > 0
    ? `direction + symbol`
    : offDirection > 0 ? `direction` : `symbol`;

  return makeObservation({
    id: `${key}:${session.id}:drift`,
    analyzer: key,
    scope: 'session',
    scopeId: session.id,
    severity,
    category: CATEGORY.PROCESS,
    title: `${pctLabel} of trades drifted off thesis (${driftDesc})`,
    detail: offPnl !== 0
      ? `Off-thesis slice was ${offPnl >= 0 ? '+' : ''}${offPnl.toFixed(0)} — ${offPnl >= 0 ? 'profit doesn\'t validate skipping the plan' : 'this is where the discipline leak showed up'}.`
      : `${totalOff} of ${trades.length} trades didn't match the stated plan.`,
    action: 'Next session: write a one-line rule for what counts as "on thesis" and stick to it — or rewrite the thesis midway rather than silently drifting.',
    evidence: { intent, thesisSymbols: Array.from(thesisSymbols), offDirection, offSymbol, offPct, offPnl },
    confidence: 0.7,
    priority: offPct >= 0.5 ? 76 : 62
  });
};

// Exports for tests
export { extractIntent, extractSymbols, knownSymbolSet, TRADING_VOCABULARY };
